import { Globe } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { statCardSurface } from "@/components/common/statCardSurface";

/**
 * Stat card with an active/inactive donut breakdown. Extracted from RegionStats
 * (structurally distinct from the icon-box StatCard — keep them separate).
 */
export interface DonutStatCardProps {
  title: string;
  total: number;
  active: number;
  inactive: number;
  icon?: LucideIcon;
  className?: string;
}

function DonutChart({
  total,
  active,
  inactive,
}: {
  total: number;
  active: number;
  inactive: number;
}) {
  const { t } = useLingui();
  const activeDeg = total > 0 ? (active / total) * 360 : 0;

  return (
    <div className="@container flex w-full items-center gap-4">
      {/* Donut kept compact — roughly the height of the active/inactive rows
          beside it — so the card stays short. Still fluid (shrinks on very
          narrow cards via container query) but capped small. */}
      <div className="relative aspect-square w-[clamp(3.5rem,28cqw,5rem)] shrink-0">
        <div
          className="size-full rounded-full"
          style={{
            background:
              total === 0
                ? "var(--gray-300)"
                : `conic-gradient(var(--color-status-active) 0deg ${activeDeg}deg, var(--gray-300) ${activeDeg}deg 360deg)`,
          }}
        />
        <div className="absolute inset-[18%] flex items-center justify-center rounded-full bg-(--color-surface-content)">
          <span className="text-[clamp(1.125rem,6cqw,1.375rem)] font-semibold leading-none text-(--color-text-primary)">
            {total}
          </span>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-2 shrink-0 rounded-full bg-(--color-status-active)" />
            <span className="text-sm text-(--color-text-secondary)">{t`Active`}</span>
          </div>
          <span className="text-sm font-medium text-(--color-text-primary)">{active}</span>
        </div>
        <div className="h-px bg-(--color-surface-accent)" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-2 shrink-0 rounded-full bg-(--color-surface-accent)" />
            <span className="text-sm text-(--color-text-secondary)">{t`Inactive`}</span>
          </div>
          <span className="text-sm font-medium text-(--color-text-primary)">{inactive}</span>
        </div>
        <div className="h-px bg-(--color-surface-accent)" />
      </div>
    </div>
  );
}

export function DonutStatCard({
  title,
  total,
  active,
  inactive,
  icon: Icon = Globe,
  className,
}: DonutStatCardProps) {
  return (
    <div
      className={cn(
        statCardSurface,
        "flex min-w-0 flex-1 flex-col gap-3 p-4",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-(--color-text-secondary)">{title}</span>
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-(--color-surface-accent)">
          <Icon className="size-4 text-(--color-text-secondary)" />
        </div>
      </div>
      <DonutChart total={total} active={active} inactive={inactive} />
    </div>
  );
}
